import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { PermissionService } from '../services/permission.service';
import { CompanyContextService } from '../services/company-context.service';
import { rememberReturnUrl } from '../auth/return-url';

// A signed-out visit to a page (a pasted or bookmarked link) remembers it, so sign-in returns there.
export const authGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  if (auth.isAuthenticated()) return true;
  rememberReturnUrl(state.url);
  return router.createUrlTree(['/auth/login']);
};

/**
 * Client-scoped routes (Sites, Tasks, CRM, …) require a SELECTED company. When none is chosen —
 * e.g. just logged in, or a page reload before re-selecting — send the user to the picker, which
 * also renders the "no company assigned" wall for zero-company users.
 */
export const companyGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  const ctx = inject(CompanyContextService);
  const router = inject(Router);
  if (!auth.isAuthenticated()) {
    rememberReturnUrl(state.url);
    return router.createUrlTree(['/auth/login']);
  }
  if (ctx.hasCompany()) return true;

  // No company in memory yet — make sure the list is loaded before deciding.
  try { await ctx.ensureLoaded(); } catch { /* fall through to the picker */ }
  if (ctx.hasCompany()) return true;
  rememberReturnUrl(state.url);   // the picker comes back here once a company is chosen
  return router.createUrlTree(['/select-company']);
};

/**
 * Blocks routes the user can't access: the tenant must have `data.module` enabled AND, when
 * `data.permission` is set, the user's role must hold it. Redirects to the dashboard otherwise.
 * Dashboard itself is intentionally left unguarded as a safe home.
 */
export const moduleGuard: CanActivateFn = (route) => {
  const permission = inject(PermissionService);
  const router = inject(Router);
  const module = route.data?.['module'] as string | undefined;
  const perm = route.data?.['permission'] as string | undefined;
  const allowed = (!module || permission.hasModule(module)) && (!perm || permission.has(perm));
  // Fall back to the unguarded dashboard route (hidden from the menu, but always accessible)
  // so an access-denied redirect can never loop through another guarded route.
  return allowed ? true : router.createUrlTree(['/dashboard']);
};

export const guestGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  if (!auth.isAuthenticated()) return true;
  return router.createUrlTree(['/dashboard']);
};
